import axios from 'axios';

// Der Schwellenwert für das Flywheel-Ereignis
// Wenn der Preisvorteil diesen Wert überschreitet, wird das Flywheel-Ereignis ausgelöst
const FLYWHEEL_THRESHOLD = 0.02; // 2% Preisvorteil
const PRICE_UPDATE_INTERVAL = 30000; // 30 seconds

// Separate update frequency for frontend vs backend monitoring
const FRONTEND_UPDATE_INTERVAL = 300000; // 5 minutes between frontend visual updates

class PriceService {
  constructor() {
    this._prices = {
      sps: 0,
      dec: 0,
      voucher: 0
    };
    this._isFlywheelActive = false;
    this._lastUpdated = null;
    this._listeners = [];
    this._burnEvents = [];
    this._burnEventListeners = [];
    
    // Track the last time we updated the frontend
    this._lastFrontendUpdate = 0;
    
    // Flag to distinguish between update types
    this._updateType = {
      FRONTEND: 'frontend',
      MONITORING: 'monitoring'
    };
    
    // Initialer Aufruf - mark as frontend update
    this.fetchPrices(this._updateType.FRONTEND);
    
    // Regelmäßige Aktualisierung für das Monitoring-Script
    this._interval = setInterval(() => {
      this.fetchPrices(this._updateType.MONITORING);
    }, PRICE_UPDATE_INTERVAL);

    // Schedule less frequent updates for the frontend
    this._frontendInterval = setInterval(() => {
      this.fetchPrices(this._updateType.FRONTEND);
    }, FRONTEND_UPDATE_INTERVAL);
  }
  
  // Preise von der Splinterlands API abrufen
  async fetchPrices(updateType = this._updateType.MONITORING) {
    const isFrontendUpdate = updateType === this._updateType.FRONTEND;
    
    try {
      const response = await axios.get("https://prices.splinterlands.com/prices");
      
      if (response.data && typeof response.data === 'object') {
        // Check if prices actually changed significantly
        let significantChange = false;
        let priceChangeThreshold = 0.0001; // Only count as change if difference is significant
        const { sps, dec, voucher } = response.data;
        
        let pricesChanged = false;
        
        if (sps !== undefined && Math.abs(this._prices.sps - sps) > priceChangeThreshold) {
          this._prices.sps = sps;
          pricesChanged = true;
          significantChange = true;
        } else if (sps !== undefined) {
          this._prices.sps = sps; // Still update but don't count as significant
          pricesChanged = true;
        }
        
        if (dec !== undefined && Math.abs(this._prices.dec - dec) > priceChangeThreshold) {
          this._prices.dec = dec;
          pricesChanged = true;
          significantChange = true;
        } else if (dec !== undefined) {
          this._prices.dec = dec; // Still update but don't count as significant
          pricesChanged = true;
        }
        
        if (voucher !== undefined && Math.abs(this._prices.voucher - voucher) > priceChangeThreshold) {
          this._prices.voucher = voucher;
          pricesChanged = true;
          significantChange = true;
        } else if (voucher !== undefined) {
          this._prices.voucher = voucher; // Still update but don't count as significant
          pricesChanged = true;
        }
        
        // Only update timestamps and check flywheel if prices actually changed
        if (pricesChanged) {
          this._lastUpdated = new Date();
          
          // Prüfen, ob das Flywheel-Event aktiv ist
          this._checkFlywheelStatus();
        }
        
        // Only notify frontend listeners on explicit frontend updates or significant price changes
        if (isFrontendUpdate || significantChange) {
          this._lastFrontendUpdate = Date.now();
          this._notifyListeners();
        }
      }
    } catch (error) {
      
    }
  }
  
  // Prüfen, ob das Flywheel-Event aktiv ist
  _checkFlywheelStatus() {
    // Die Logik für das Flywheel-Event:
    // Wenn es profitabler ist, SPS zu DEC zu konvertieren als SPS zu verkaufen
    const decValuePer1000 = this._prices.dec * 1000;
    const spsNeededFor1000Dec = decValuePer1000 / this._prices.sps;
    
    // Only update if the state actually changed to avoid unnecessary renders
    const newFlywheelState = spsNeededFor1000Dec && spsNeededFor1000Dec < (1000 * (1 - FLYWHEEL_THRESHOLD));
    
    if (this._isFlywheelActive !== newFlywheelState) {
      this._isFlywheelActive = newFlywheelState;
    }
  }
  
  // Ein neues Burn-Ereignis registrieren
  registerBurnEvent(burnData) {
    const burnEvent = {
      ...burnData,
      timestamp: burnData.timestamp || new Date().toISOString()
    };
    
    // Check if this event is unique to prevent duplicate animations
    const isDuplicate = this._burnEvents.some(
      event => event.timestamp === burnEvent.timestamp && 
              event.spsBurned === burnEvent.spsBurned &&
              event.username === burnEvent.username
    );
    
    if (!isDuplicate) {
      this._burnEvents.unshift(burnEvent); // Neuestes Event an den Anfang
      if (this._burnEvents.length > 10) {
        this._burnEvents.pop(); // Ältestes Event entfernen, wenn mehr als 10
      }
      
      // Force this to count as a frontend update to show animations
      this._lastFrontendUpdate = Date.now();
      
      // Alle Burn-Event-Listener informieren
      this._notifyBurnEventListeners(burnEvent);
    }
  }
  
  // Listener für Preisänderungen registrieren
  subscribe(callback) {
    this._listeners.push(callback);
    
    // Sofort den aktuellen Status mitteilen
    callback({
      prices: { ...this._prices },
      isFlywheelActive: this._isFlywheelActive,
      lastUpdated: this._lastUpdated
    });
    
    // Unsubscribe-Funktion zurückgeben
    return () => {
      this._listeners = this._listeners.filter(listener => listener !== callback);
    };
  }
  
  // Listener für Burn-Events registrieren
  subscribeToBurnEvents(callback) {
    this._burnEventListeners.push(callback);
    
    // Unsubscribe-Funktion zurückgeben
    return () => {
      this._burnEventListeners = this._burnEventListeners.filter(listener => listener !== callback);
    };
  }
  
  // Alle Preis-Listener benachrichtigen
  _notifyListeners() {
    const data = {
      prices: { ...this._prices },
      isFlywheelActive: this._isFlywheelActive,
      lastUpdated: this._lastUpdated
    };
    
    this._listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
      }
    });
  }
  
  // Alle Burn-Event-Listener benachrichtigen
  _notifyBurnEventListeners(burnEvent) {
    this._burnEventListeners.forEach(callback => {
      try {
        callback(burnEvent);
      } catch (error) {
      }
    });
  }
  
  // Die letzten Burn-Events abrufen
  getRecentBurnEvents() {
    return [...this._burnEvents];
  }
  
  // Reinigung beim Beenden
  cleanup() {
    if (this._interval) {
      clearInterval(this._interval);
    }
    if (this._frontendInterval) {
      clearInterval(this._frontendInterval);
    }
  }
  
  // Aktuelle Daten abfragen ohne Aktualisierung auszulösen
  getData() {
    return {
      prices: { ...this._prices },
      isFlywheelActive: this._isFlywheelActive,
      lastUpdated: this._lastUpdated
    };
  }
  
  // Force a manual frontend update - only call this when user interaction requires it
  async forceUiUpdate() {
    await this.fetchPrices(this._updateType.FRONTEND);
    return this.getData();
  }
  
  // For monitoring script use - updates prices without notifying UI
  async updatePricesForMonitoring() {
    await this.fetchPrices(this._updateType.MONITORING);
    return this.getData();
  }
}

// Singleton-Instanz exportieren
const priceService = new PriceService();
export default priceService;